import { ICheckboxProps } from "@/Common/Components/Inputs/CheckboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { IAdditionalElement, InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { Locale } from "@/Common/Models/Localization/Locale";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";

class TextboxRenderOptions {
    public value: string;
    public maxLength?: number;
    public isNumeric?: boolean;
    public isInteger?: boolean;
    public min?: number;
    public max?: number;
    public isReadOnlyOverride?: boolean;
    public className?: string;
    public containerClassName?: string;
    public placeholder?: string;
    public debounceMs?: number;
    public id?: string;
    public onChange?: (value: string) => void;
    public onFocus?: () => void;
    public onBlur?: (event?: FocusEvent) => void;
    public onKeyDown?: (event: React.KeyboardEvent) => void;
    public clickableTextOptions?: ClickableTextRenderOptions;
    public locale?: Locale;
    public disabled?: boolean;
    public tabIndex?: number;
    // TODO: change type for HTMLInputTypeAttribute when migration to React 17 is done.
    public type?: "button" | "checkbox" | "color" | "date" | "datetime-local" | "email" | "file" | "hidden" | "image" | "month" | "number" | "password" | "radio" | "range" | "reset" | "search" | "submit" | "tel" | "text" | "time" | "url" | "week";
    public style?: React.CSSProperties;
}

class ClickableTextRenderOptions {
    public onClick: (show: boolean) => void;
    public show: boolean;
    public context: string;
    public leftAlign?: boolean;
    public selectFromListCssClass?: string;
}

class TextAreaRenderOptions {
    public value: string;
    public onChange?: (value: string) => void;
    public id?: string;
    public maxLength?: number;
    public maxRows?: number;
    public className?: string;
    public clickableTextOptions?: ClickableTextRenderOptions;
}

class TextAreaResizableRenderOptions extends TextAreaRenderOptions {
    public minHeight?: string;
    public maxHeight?: string;
    public isDisabled?: boolean;
    public containerCssClass?: string;
    public clickableTextOptions?: ClickableTextRenderOptions;
}

class TextAreaResizableWithAutocompleteRenderOptions extends TextAreaResizableRenderOptions {
    public autoCompleteItems: SearchableItemModelT<string>[];
    public onAutoCompleteItemSelected?: (selectedText: string) => void;
}

class AutoCompleteTextboxRenderOptions<TModel> {
    public id?: string;
    public value: string;
    public autoCompleteItems: SearchableItemModelT<TModel>[];
    public onChange?: (value: string) => void;
    public onAutoCompleteSelected?: (code, model, value) => void;
    public onFocus?: () => void;
    public onBlur?: (event?: FocusEvent) => void;
    public maxLength?: number;
    public isNumeric?: boolean;
    public min?: number;
    public max?: number;
    public className?: string;
    public listWidth?: string;
    public isReadOnlyOverride?: boolean;
    public disableMatchAutoSorting?: boolean;
    public debounceMs?: number;
    public disabled?: boolean;
    public tabIndex?: number;
    // TODO: change type for HTMLInputTypeAttribute when migration to React 17 is done.
    public type?: "button" | "checkbox" | "color" | "date" | "datetime-local" | "email" | "file" | "hidden" | "image" | "month" | "number" | "password" | "radio" | "range" | "reset" | "search" | "submit" | "tel" | "text" | "time" | "url" | "week";
}

class DateSelectorRenderOptions {
    public value: Date;
    public onChange?: (value: Date) => void;
    public id?: string;
    public className?: string;
    public containerClassName?: string;
    public isValid?: boolean = true;
    public dateFormatWhenReadonly?: string = "YYYY-MM-DD";
    public dateFormat?: string = "yyyy-MM-dd";
    public isMonthPicker?: boolean = false;
    public width?: number;
    public height?: number;
    public maxDate?: Date;
    public ignoreInvalidCharacters?: boolean;
    public ref?: React.Ref<DateSelector>;

    public constructor(fields?: Partial<DateSelectorRenderOptions>) {
        Object.assign(this, fields);
    }
}

class TextboxControlWithDatePickerRenderOption {
    public date: Date;
    public precision: LifeStageDatePrecisionEnum;
    public onChange?: (value: DateTimeWithPrecision) => void;
    public id?: string;
    public className?: string;
    public width?: number;
    public isMonthPicker?: boolean;

    public constructor(fields?: Partial<TextboxControlWithDatePickerRenderOption>) {
        Object.assign(this, fields);
    }
}

class InlineRadioOptions<T> {
    public value: T | null;
    public items: InlineOption<T>[];
    public onChange: (value: T) => void;
    public id: string;
    public className?: string;
    public additional?: IAdditionalElement[];
    public allowDeselect?: boolean;
    public additionalWrapperClassName?: string;
    public optionSpacing?: string;
    public heightInPixels?: number;
    public wrapOptions?: boolean;
}

class CheckboxOptions implements ICheckboxProps {
    // from ICheckboxProps
    public checked: boolean;
    public id?: string;
    public operation?: number;
    public target?: CheckboxTarget;
    public label?: string;
    public maxLen?: number;
    public style?: React.CSSProperties;
    public labelStyle?: any;
    public item?: any;
    public extraItem?: any;
    public className?: string;
    public DivWrap?: boolean;
    public idModifier?: string;
    public disabled?: boolean;
    public onChange?: (isChecked: boolean) => void;
    public wrapSpan?: boolean;
    public title?: string;
    public labelClassName?: string;
    public operationArg1?: any;
    public operationArg2?: any;
    public operationArg3?: any;
    public justifyContent?: "flex-start" | "flex-end" | "center" | "space-between" | "space-around" | "space-evenly" | "stretch";

    // other options
    public containerClassName?: string;
    public containerStyle?: React.CSSProperties;
}

export { AutoCompleteTextboxRenderOptions, CheckboxOptions, ClickableTextRenderOptions, DateSelectorRenderOptions, InlineRadioOptions, TextAreaRenderOptions, TextAreaResizableRenderOptions, TextAreaResizableWithAutocompleteRenderOptions, TextboxControlWithDatePickerRenderOption, TextboxRenderOptions };
